import type { Category, Entity, ItemPurchase } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { daysBetween, entryDayLabel, karachiDate, nearDayKey } from '../lib/dates.ts';
import { groupDigits, rs } from '../lib/money.ts';
import { CategoryIcon, Icon, type IconName } from '../ui/icons.tsx';
import { Num } from '../ui/parts.tsx';
import { amountOf, available, isOverBalance, progress, type Draft, type Step } from './draft.ts';
import { categoryLabel, tintOf } from './labels.ts';

interface SlipProps {
  draft: Draft;
  entity: Entity;
  balance: number;
  category: Category | undefined;
  step: Step | null;
  flash: boolean;
  lastBought: ItemPurchase | null;
  /** Another company / house paid the shop (Dues): shown instead of "Left after". */
  payerName?: string | null;
  onRow: (row: 'item' | 'amount' | 'proof') => void;
}

/** The entry being built, shown as a paper slip. Each row is tappable to edit it. */
export function Slip({ draft, entity, balance, category, step, flash, lastBought, payerName, onRow }: SlipProps) {
  const { t, lang } = useT();
  const p = progress(draft, balance);
  const amt = amountOf(draft);
  const over = isOverBalance(draft, balance);
  const left = available(draft, balance) - amt;
  const today = karachiDate();
  const active = (row: 'item' | 'amount' | 'proof'): boolean => (row === 'item' ? step === 'what' || step === 'item' : step === row);

  const rowClass = (row: 'item' | 'amount' | 'proof') =>
    `flex w-full items-center gap-3 border-s-[5px] px-3 text-start transition-colors duration-500 ${
      active(row) ? 'border-ink' : 'border-transparent'
    } ${flash ? 'bg-[#FEF9C3]' : active(row) ? 'bg-ground' : 'bg-paper'}`;

  // Phones (low: up to 860 px tall, tight: 700 px and under) show only the rows that matter for
  // the step, so each step fits one screen without scrolling:
  //   item step → item; amount step → item + amount (tight: amount); receipt step → item, amount
  //   and receipt (tight: receipt). The last-bought line goes on the amount and receipt steps.
  const later = step === 'amount' || step === 'proof';
  const hideItem = later ? 'tight:hidden' : '';
  const hideLast = later ? 'low:hidden' : '';
  const hideAmount = step === 'item' ? 'low:hidden' : step === 'proof' ? 'tight:hidden' : '';
  const hideProof = step === 'item' || step === 'amount' ? 'low:hidden' : '';

  const proofIcon: IconName = draft.noReceipt && draft.photos.length === 0 ? 'noReceipt' : 'camera';
  const proofText =
    draft.photos.length > 0
      ? t('add.photos', { count: draft.photos.length })
      : draft.noReceipt
        ? t('add.noReceiptShort')
        : t('add.noPhotoYet');

  const near = nearDayKey(draft.txnDate, today);
  const dayText = near ? t(near) : entryDayLabel(draft.txnDate, today, lang);

  return (
    <section
      aria-label={t('add.whatTitle')}
      className="mx-3 mt-3 shrink-0 overflow-hidden rounded-2xl border-[1.5px] low:mt-2 border-line-strong bg-paper split:mx-0 split:mt-0"
    >
      <button type="button" onClick={() => onRow('item')} className={`${rowClass('item')} min-h-16 py-2 low:min-h-14 ${hideItem}`}>
        <span
          className="flex size-[42px] shrink-0 items-center justify-center rounded-xl"
          style={{ background: tintOf(entity.colour), color: entity.colour }}
        >
          <CategoryIcon icon={category?.icon ?? null} size={24} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col leading-snug">
          <span className={`truncate text-fluid-lg font-bold rtl:text-[clamp(16px,4.8vw,19px)] ${draft.itemName ? '' : 'text-muted'}`}>
            {draft.itemName || (category ? `${categoryLabel(category, t)}…` : t('add.itemPlaceholder'))}
          </span>
          {category && <span className="text-[13px] text-muted">{categoryLabel(category, t)}</span>}
        </span>
        {draft.quantity > 0 && draft.unit && (
          <span className="text-base font-semibold whitespace-nowrap">
            <Num>{draft.quantity}</Num> {t(`units.${draft.unit}`)}
          </span>
        )}
        <DoneMark done={p.itemDone} />
      </button>

      {lastBought && (
        <p
          className={`flex items-start gap-2 border-t border-line bg-[#F8FAFC] px-3.5 py-1.5 text-sm leading-snug text-[#374151] ${hideLast}`}
        >
          <Icon name="clock" size={16} className="mt-0.5" />
          <span>
            {t('add.lastBought', {
              ago:
                daysBetween(lastBought.txnDate, today) === 0
                  ? t('ago.today')
                  : daysBetween(lastBought.txnDate, today) === 1
                    ? t('ago.yesterday')
                    : t('ago.days', { count: daysBetween(lastBought.txnDate, today) }),
              what: lastBought.quantity ? `${lastBought.quantity} ${t(`units.${lastBought.unit ?? 'pcs'}`)}` : '—',
              amount: rs(lastBought.amount),
              who: lastBought.spenderName,
            })}
          </span>
        </p>
      )}

      <button
        type="button"
        // On the How much step the amount is typed in its own field: a tap here goes there.
        onClick={() => (step === 'amount' ? document.getElementById('amount')?.focus() : onRow('amount'))}
        className={`${rowClass('amount')} min-h-[76px] border-t border-t-line py-1.5 short:min-h-16 low:min-h-16 tight:min-h-14 ${hideAmount} ${step === 'amount' ? 'tight:border-t-0' : ''}`}
      >
        <span className="num flex items-center gap-1.5">
          <span className="text-[clamp(16px,5vw,20px)] font-semibold text-muted">Rs</span>
          {/* Shrinks on 320 px phones so "Rs 500,000" and "Left after" still share the row. */}
          <span
            className={`text-[clamp(28px,11vw,40px)] leading-tight font-bold tracking-tight short:text-[32px] ${amt ? (over ? 'text-danger' : 'text-ink') : 'text-key'}`}
          >
            {/* A summary, not a field: no "0" and no blinking caret that look like a place to type. */}
            {amt ? groupDigits(amt) : '—'}
          </span>
        </span>
        <span className="flex-1" />
        {draft.paidBy ? (
          // Another house paid: his cash is untouched, so "Left after" would mislead.
          <span className="flex max-w-[55%] flex-col items-end leading-snug">
            <span className="text-[13px] font-semibold text-muted">{t('add.whoPaid')}</span>
            <span className="truncate text-fluid-base font-bold">{payerName ?? t('history.paidByOther')}</span>
          </span>
        ) : (
          <span className="flex flex-col items-end leading-snug">
            <span className={`text-[13px] font-semibold ${over ? 'text-danger' : 'text-muted'}`}>
              {over ? t('add.notEnough') : t('add.leftAfter')}
            </span>
            <Num className={`text-fluid-base font-bold ${over ? 'text-danger' : left < 2000 ? 'text-warning' : 'text-ink'}`}>{rs(left)}</Num>
          </span>
        )}
        <DoneMark done={p.amountDone} />
      </button>

      <button
        type="button"
        onClick={() => onRow('proof')}
        className={`${rowClass('proof')} min-h-14 border-t border-t-line py-1.5 low:min-h-12 ${hideProof} ${step === 'proof' ? 'tight:border-t-0' : ''}`}
      >
        <span className={draft.photos.length ? 'text-ink' : draft.noReceipt ? 'text-warning-text' : 'text-muted'}>
          <Icon name={proofIcon} />
        </span>
        <span
          className={`min-w-0 flex-1 truncate text-[15px] font-semibold ${draft.photos.length ? 'text-ink' : draft.noReceipt ? 'text-warning-text' : 'text-muted'}`}
        >
          {proofText}
        </span>
        <span className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-ground px-2.5 text-sm font-semibold">
          <Icon name="calendar" size={14} />
          {dayText}
        </span>
        <DoneMark done={p.proofDone} />
      </button>
    </section>
  );
}

function DoneMark({ done }: { done: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-white transition-colors ${
        done ? 'border-success bg-success' : 'border-[#D1D5DB] bg-transparent'
      }`}
    >
      <Icon name="check" size={13} stroke={3.5} />
    </span>
  );
}
